import { useEffect, useState } from "react"
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router"
import { toast } from "sonner"
import { Logo } from "@/components/logo"
import { authClient, safeReturnPath, signOutSucceeded } from "@/lib/auth-client"
import { errorMessage } from "@/lib/errors"
import { useAuthGate } from "@/hooks/use-auth-gate"

export const Route = createFileRoute("/admin_/login")({
  validateSearch: (search: Record<string, unknown>): { returnTo?: string } => ({
    returnTo: typeof search.returnTo === "string" ? search.returnTo : undefined,
  }),
  component: AdminLoginPage,
})

function adminReturnPath(path: string | undefined) {
  const safePath = safeReturnPath(path)
  return safePath === "/admin" || safePath?.startsWith("/admin/")
    ? safePath
    : "/admin"
}

function AdminLoginPage() {
  const { returnTo } = Route.useSearch()
  const gate = useAuthGate()
  const navigate = useNavigate()

  useEffect(() => {
    if (gate.status === "ready" && gate.profile.isAdmin) {
      void navigate({ to: adminReturnPath(returnTo) })
    }
  }, [gate, navigate, returnTo])

  if (gate.status === "ready" && !gate.profile.isAdmin) {
    return <UnauthorizedAccount />
  }

  if (gate.status === "no-profile") {
    return <UnauthorizedAccount />
  }

  return (
    <div className="mkt-auth home-wf">
      <Link to="/" className="home-wf-brand" aria-label="Hire home">
        <Logo />
      </Link>
      <div className="mkt-auth-card">
        <AdminLoginForm />
      </div>
    </div>
  )
}

function AdminLoginForm() {
  const [username, setUsername] = useState("")
  const [password, setPassword] = useState("")
  const [loading, setLoading] = useState(false)

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    setLoading(true)
    try {
      const result = await authClient.signIn.username({ username, password })
      if (result.error) throw new Error(result.error.message)
    } catch (error) {
      toast.error(errorMessage(error, "Could not sign in"))
      setLoading(false)
    }
  }

  return (
    <div className="mkt-auth-panel">
      <header className="mkt-auth-head">
        <h1 className="mkt-auth-title">Admin sign in</h1>
        <p className="mkt-auth-desc">Use your administrator username and password.</p>
        <p className="mkt-auth-proof" aria-label="Admin access">
          <span>Platform operators only</span>
        </p>
      </header>

      <form className="mkt-auth-actions" onSubmit={onSubmit}>
        <div className="mkt-auth-field">
          <label htmlFor="admin-username">Username</label>
          <input
            id="admin-username"
            value={username}
            onChange={(event) => setUsername(event.target.value)}
            placeholder="admin"
            required
            minLength={3}
            autoComplete="username"
            autoCapitalize="none"
            spellCheck={false}
          />
        </div>

        <div className="mkt-auth-field">
          <label htmlFor="admin-password">Password</label>
          <input
            id="admin-password"
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            required
            minLength={8}
            autoComplete="current-password"
          />
        </div>

        <button type="submit" className="mkt-auth-submit" disabled={loading}>
          {loading ? "Signing in…" : "Sign in"}
        </button>
      </form>
    </div>
  )
}

function UnauthorizedAccount() {
  const navigate = useNavigate()
  const [signingOut, setSigningOut] = useState(false)

  const signOut = async () => {
    setSigningOut(true)
    if (!(await signOutSucceeded())) {
      toast.error("Could not sign out. Please try again.")
      setSigningOut(false)
      return
    }
    void navigate({ to: "/admin/login" })
  }

  return (
    <div className="mkt-auth home-wf">
      <Link to="/" className="home-wf-brand" aria-label="Hire home">
        <Logo />
      </Link>
      <div className="mkt-auth-card">
        <div className="mkt-auth-panel">
          <header className="mkt-auth-head">
            <h1 className="mkt-auth-title">Not authorized</h1>
            <p className="mkt-auth-desc">
              This account does not have administrator access.
            </p>
          </header>
          <div className="mkt-auth-actions">
            <button type="button" className="mkt-auth-submit" onClick={() => void signOut()} disabled={signingOut}>
              {signingOut ? "Signing out…" : "Use a different account"}
            </button>
            <Link to="/" className="mkt-auth-foot text-center">
              Back to Hire
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}
